import { useMemo, useState, type PointerEvent } from 'react';
import { onIntervals, type MachineSeries } from 'tf-pulse-shared';
import { toBinarySignalState } from '../../utils/booleanSignals';
import { AlarmTriggerMarker } from './AlarmTriggerMarker';
import { ChartTooltip, type ChartTooltipRow } from './ChartTooltip';
import {
  CHART_COLORS,
  CHART_PLOT_HEIGHT,
  describeMachinesWithoutData,
  formatPointerTime,
  timeToX,
  useContainerWidth,
  xToTime,
} from './chartGeometry';
import type { TimeBounds } from './timeRangeDomain';

/** Room above the first lane for the alarm bell */
const MARKER_HEADROOM = 20;
const TOP_PADDING = 4;
/** Below this lane height the machine name no longer fits inside the lane */
const MIN_LABELLED_LANE_HEIGHT = 14;

interface MachineLaneChartProps {
  /** One entry per selected machine, in selection order */
  series: MachineSeries[];
  /** The window the X-axis spans */
  timeBounds: TimeBounds;
  /** Labels for the X-axis, spread evenly across the window */
  xAxisLabels: string[];
  /** Optional moment (ms) the alarm fired, drawn across every lane */
  alarmTriggerTime?: number;
}

/**
 * MachineLaneChart - one on/off lane per machine for a boolean signal
 *
 * Each reporting machine keeps its own row, so an alarm that is on for one
 * machine stays visible next to machines where it is off. Nothing is averaged
 * across machines. Machines without a sample in the window fold into one
 * legend line instead of an empty lane each. The lanes always share the fixed
 * plot height of the other charts and never scroll: rows get thinner as
 * machines are added, and the machine name sits inside its lane.
 */
export function MachineLaneChart({
  series,
  timeBounds,
  xAxisLabels,
  alarmTriggerTime,
}: MachineLaneChartProps) {
  const [containerRef, containerWidth] = useContainerWidth<HTMLDivElement>();
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const { machinesWithData, machinesWithoutData } = useMemo(
    () => ({
      machinesWithData: series.filter((machine) => machine.points.length > 0),
      machinesWithoutData: series.filter(
        (machine) => machine.points.length === 0
      ),
    }),
    [series]
  );

  const markerTime =
    alarmTriggerTime !== undefined &&
    alarmTriggerTime >= timeBounds.from &&
    alarmTriggerTime <= timeBounds.to
      ? alarmTriggerTime
      : undefined;
  const top = markerTime !== undefined ? MARKER_HEADROOM : TOP_PADDING;

  // Divide the fixed plot height over the lanes; more lanes, thinner rows.
  const laneCount = Math.max(machinesWithData.length, 1);
  const laneGap = laneCount > 4 ? 4 : 8;
  const laneHeight = Math.max(
    1,
    Math.floor(
      (CHART_PLOT_HEIGHT - top - (laneCount - 1) * laneGap) / laneCount
    )
  );
  const showNames = laneHeight >= MIN_LABELLED_LANE_HEIGHT;

  const lanes = useMemo(
    () =>
      machinesWithData.map((machine, index) => ({
        ...machine,
        y: top + index * (laneHeight + laneGap),
        intervals: onIntervals(
          machine.points,
          (point) => toBinarySignalState(point.average) === 1,
          timeBounds.to
        ),
      })),
    [machinesWithData, top, laneHeight, laneGap, timeBounds.to]
  );

  const toX = (time: number) => timeToX(time, timeBounds, containerWidth);

  const handlePointerMove = (event: PointerEvent<SVGSVGElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setPointer({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  const hoverTime = pointer
    ? xToTime(pointer.x, timeBounds, containerWidth)
    : undefined;
  const hoverRows: ChartTooltipRow[] =
    hoverTime === undefined
      ? []
      : lanes.map((lane) => {
          const isOn = lane.intervals.some(
            (interval) => hoverTime >= interval.from && hoverTime < interval.to
          );
          return {
            key: lane.id,
            label: lane.label,
            value: isOn ? 'on' : 'off',
            color: isOn ? CHART_COLORS.on : undefined,
            muted: !isOn,
          };
        });

  return (
    <div className='flex flex-col gap-2.5 select-none'>
      {/* Lanes, fixed height */}
      <div ref={containerRef} className='relative h-40'>
        <svg
          width='100%'
          height={CHART_PLOT_HEIGHT}
          className='block overflow-visible'
          onPointerMove={handlePointerMove}
          onPointerLeave={() => setPointer(null)}
        >
          {lanes.map((lane) => (
            <g key={lane.id}>
              <rect
                x={0}
                y={lane.y}
                width='100%'
                height={laneHeight}
                fill={CHART_COLORS.track}
              />
              <line
                x1={0}
                x2='100%'
                y1={lane.y + laneHeight}
                y2={lane.y + laneHeight}
                stroke={CHART_COLORS.grid}
                strokeWidth={1}
              />
              {lane.intervals.map((interval) => {
                const x = toX(interval.from);
                const width = Math.max(1, toX(interval.to) - x);
                return (
                  <rect
                    key={interval.from}
                    x={x}
                    y={lane.y}
                    width={width}
                    height={laneHeight}
                    rx={2}
                    fill={CHART_COLORS.on}
                  />
                );
              })}
            </g>
          ))}

          {markerTime !== undefined && (
            <AlarmTriggerMarker
              x={toX(markerTime)}
              y1={top}
              y2={CHART_PLOT_HEIGHT}
              time={markerTime}
              containerWidth={containerWidth}
            />
          )}

          {pointer && (
            <line
              x1={pointer.x}
              x2={pointer.x}
              y1={top}
              y2={CHART_PLOT_HEIGHT}
              stroke={CHART_COLORS.crosshair}
              strokeWidth={1}
            />
          )}
        </svg>

        {/* Machine names inside their lane, as HTML so they truncate */}
        {showNames &&
          lanes.map((lane) => (
            <div
              key={lane.id}
              className='pointer-events-none absolute left-1 flex max-w-[60%]
                items-center'
              style={{ top: lane.y, height: laneHeight }}
            >
              <span
                className='size-caption text-secondary bg-surface/85 truncate
                  rounded-sm px-1 leading-none'
                title={lane.label}
              >
                {lane.label}
              </span>
            </div>
          ))}

        {pointer && hoverTime !== undefined && (
          <ChartTooltip
            x={pointer.x}
            y={pointer.y}
            containerWidth={containerWidth}
            title={formatPointerTime(hoverTime, timeBounds)}
            rows={hoverRows}
          />
        )}
      </div>

      {/* X-axis labels and legend */}
      <div className='flex flex-col gap-2'>
        <div
          className='size-caption text-secondary flex items-center
            justify-between'
        >
          {xAxisLabels.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>
        {machinesWithoutData.length > 0 && (
          <div className='size-caption text-secondary flex items-center gap-4'>
            <span
              className='flex min-w-0 items-center gap-1.5'
              title={machinesWithoutData
                .map((machine) => machine.label)
                .join(', ')}
            >
              <span
                aria-hidden
                className='inline-block h-0.5 w-3 shrink-0 rounded-full'
                style={{ backgroundColor: CHART_COLORS.noData }}
              />
              <span className='truncate'>
                {describeMachinesWithoutData(machinesWithoutData.length)}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
